import React, { useState } from "react";
import { ExternalLink, Github, Search } from "lucide-react";
import { projects } from "../data/mock";
import { Link } from "react-router-dom";

const Projects = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const categories = [
    "All",
    ...new Set(projects.map((project) => project.category)),
  ];

  const filteredProjects = projects.filter((project) => {
    const matchesSearch =
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.technologies.some((tech) =>
        tech.toLowerCase().includes(searchTerm.toLowerCase())
      );
    const matchesCategory =
      selectedCategory === "All" || project.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getColorClasses = (bgColor) => {
    const colorMap = {
      "light-pink":
        "bg-pink-100 dark:bg-pink-900/30 border-pink-200 dark:border-pink-700",
      "mid-blue":
        "bg-blue-100 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700",
      "light-yellow":
        "bg-yellow-100 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-700",
      "mid-purple":
        "bg-purple-100 dark:bg-purple-900/30 border-purple-200 dark:border-purple-700",
      "mid-green":
        "bg-green-100 dark:bg-green-900/30 border-green-200 dark:border-green-700",
    };
    return (
      colorMap[bgColor] ||
      "bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700"
    );
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Freelance QA": "bg-purple-600",
      "Automation Testing": "bg-pink-500",
      "Automation Flow": "bg-blue-500",
      "Backend Project": "bg-green-500",
      //'Performance Testing': 'bg-green-500',
      //'Mobile Testing': 'bg-yellow-500',
      //'Security Testing': 'bg-purple-500',
      //'Data Testing': 'bg-green-500'
    };
    return colors[category] || "bg-gray-500";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900 py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 animate-slide-in">
            My Projects
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto animate-fade-in">
            Freelance QA engagements, release quality ownership, and automation projects.
          </p>
        </div>

        {/* Search and Filter Section */}
        <div className="mb-12 space-y-6">
          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              aria-label="Search projects"
              placeholder="Search projects, technologies..."
              className="w-full pl-12 pr-4 py-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-lg"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Category Filter */}
          <div className="flex justify-center">
            <div className="flex flex-wrap gap-3 bg-white dark:bg-gray-800 p-2 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    selectedCategory === category
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg transform scale-105"
                      : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <div
              key={project.id}
              className={`project-card rounded-3xl p-8 shadow-xl border-2 hover:shadow-2xl transition-all duration-300 transform hover:scale-105 animate-fade-in-stagger ${getColorClasses(
                project.bgColor
              )}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Category Badge */}
              <div className="flex justify-between items-start mb-6">
                <span
                  className={`inline-block px-4 py-2 rounded-full text-white text-sm font-medium ${getCategoryColor(
                    project.category
                  )}`}
                >
                  {project.category}
                </span>
              </div>

              {/* Project Content */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                  {project.title}
                </h3>
                {project.role && <p className="text-sm font-semibold text-purple-700 dark:text-purple-300 mb-4">{project.role}</p>}
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                  {project.description}
                </p>

                {project.highlights && <ul className="list-disc pl-5 space-y-3 text-gray-700 dark:text-gray-300 mb-6">{project.highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}</ul>}
                {project.confidentiality && <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">{project.confidentiality}</p>}
                {/* Technologies */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {project.technologies.map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className="px-3 py-1 bg-white/50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 text-sm rounded-full border border-gray-300 dark:border-gray-600"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* Project Links */}
              <div className="flex space-x-4 mt-auto">
                {(project.website || (project.isVisible && project.liveDemo)) && (
                  <a href={project.website || project.liveDemo} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-5 py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700">
                    <ExternalLink className="w-4 h-4 mr-2" />{project.website ? "Client Website" : "Live Demo"}
                  </a>
                )}
                {project.github && (
                  <a href={project.github} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-5 py-3 bg-gray-900 dark:bg-gray-700 text-white rounded-full font-medium hover:bg-gray-800">
                    <Github className="w-4 h-4 mr-2" />GitHub
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* No Results Message */}
        {filteredProjects.length === 0 && (
          <div className="text-center py-20">
            <div className="text-6xl mb-6">🔍</div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              No projects found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Try adjusting your search terms or filter criteria
            </p>
          </div>
        )}

        {/* Call to Action */}
        <div className="text-center mt-20">
          <div className="bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-3xl p-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Interested in collaborating?
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
              Let's discuss how I can help ensure the quality of your next
              project
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-lg"
            >
              Get In Touch
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Projects;
